import { Fragment, useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import QuizFeedback from "@/components/storyboard/quiz/QuizFeedback";
import QuizEntryCounter from "@/components/storyboard/quiz/QuizEntryCounter";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import TaskInstructionHint from "@/components/storyboard/TaskInstructionHint";
import { QuizActionButton } from "@/components/storyboard/quiz/QuizActionButton";
import { useScrollToTopOnSubmit } from "@/hooks/useScrollToTopOnSubmit";
import { getTextParts } from "@/components/storyboard/quiz/utils/quizUtils";
import { buildDropdownEntryReview } from "@/components/storyboard/quiz/review/buildEntryReview";
import type { QuizDropdownEntryData } from "@/types/storyboard";
import type { InteractiveElementScore } from "@/lib/interactiveElementState";
import type { QuizReview, QuizReviewItem } from "@/lib/quizReview";
import {
  advanceAfterCollect,
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

export interface QuizGapwordDropdownState {
  currentIndex: number;
  userAnswers: string[];
  isSubmitted: boolean;
  isCorrect: boolean | null;
  correctCount: number;
}

export interface QuizGapwordDropdownProps extends HiddenVerdictProps {
  entries: QuizDropdownEntryData[];
  onValidate: (userAnswers: string[], entryIndex: number) => Promise<boolean>;
  onComplete?: (score: InteractiveElementScore, review: QuizReview) => void;
  initialPartial?: QuizGapwordDropdownState;
  initialIndex?: number;
  onPartialChange?: (partial: QuizGapwordDropdownState, index: number) => void;
}

const QuizGapwordDropdown = ({
  entries,
  onValidate,
  onComplete,
  initialPartial,
  initialIndex,
  onPartialChange,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizGapwordDropdownProps) => {
  const [state, setState] = useState<QuizGapwordDropdownState>(() => {
    if (initialPartial) return initialPartial;
    return {
      currentIndex: initialIndex ?? 0,
      userAnswers: [],
      isSubmitted: false,
      isCorrect: null,
      correctCount: 0,
    };
  });

  const hasInteractedRef = useRef<boolean>(initialPartial != null);
  const reviewRef = useRef<QuizReviewItem[]>([]);

  useEffect(() => {
    if (!hasInteractedRef.current) return;
    onPartialChange?.(state, state.currentIndex);
  }, [state, onPartialChange]);

  const { currentIndex, userAnswers, isSubmitted, isCorrect } = state;

  const contentRef = useScrollToTopOnSubmit<HTMLDivElement>(isSubmitted);

  if (!entries || entries.length === 0) {
    return null;
  }

  const currentEntry = entries[currentIndex];

  if (!currentEntry) {
    return null;
  }

  const gaps = currentEntry.gaps ?? [];
  const textParts = getTextParts(currentEntry.clozeText, currentEntry.gapMark);
  const numberOfGaps = textParts.length - 1;
  const hasAllAnswers =
    userAnswers.length === numberOfGaps &&
    userAnswers.every((answer) => answer && answer.trim() !== "");

  const handleInputChange = (gapIndex: number, value: string) => {
    if (isSubmitted) return;

    hasInteractedRef.current = true;
    setState((prev) => {
      const newAnswers = [...prev.userAnswers];
      newAnswers[gapIndex] = value;
      return { ...prev, userAnswers: newAnswers };
    });
  };

  const submitLabel = collectsWithoutVerdict(revealFeedback)
    ? (advanceLabel ?? "Nächste Frage")
    : "Überprüfen";

  const handleSubmit = async () => {
    if (!hasAllAnswers) return;

    if (collectsWithoutVerdict(revealFeedback)) {
      await advanceAfterCollect(
        () => onValidate(userAnswers, currentIndex),
        onAdvance
      );
      return;
    }

    const correct = await onValidate(userAnswers, currentIndex);
    const correctCount = state.correctCount + (correct ? 1 : 0);

    reviewRef.current[currentIndex] = buildDropdownEntryReview(
      currentEntry,
      userAnswers,
      correct
    );

    hasInteractedRef.current = true;
    setState((prev) => ({
      ...prev,
      isSubmitted: true,
      isCorrect: correct,
      correctCount,
    }));

    if (currentIndex === entries.length - 1) {
      onComplete?.(
        { correct: correctCount, total: entries.length },
        { items: reviewRef.current }
      );
      if (correct) {
        confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < entries.length - 1) {
      setState((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
        userAnswers: [],
        isSubmitted: false,
        isCorrect: null,
      }));
    }
  };

  const getSelectStyles = (gapIndex: number) => {
    if (isSubmitted) {
      const correctAnswer = gaps[gapIndex]?.correctAnswer;
      const isGapCorrect = userAnswers[gapIndex] === correctAnswer;
      return isGapCorrect
        ? "border-success bg-success-light"
        : "border-error bg-error-light";
    }
    if (userAnswers[gapIndex]) {
      return "border-secondary bg-secondary/10";
    }
    return "border-gray-200 bg-white hover:border-gray-300";
  };

  return (
    <div className="flex h-full flex-col">
      <QuizEntryCounter currentIndex={currentIndex} total={entries.length}>
        <div className="prose quiz-question max-w-full font-semibold text-primary">
          <CourseMarkdown>{currentEntry.instruction}</CourseMarkdown>
        </div>
      </QuizEntryCounter>

      <div
        ref={contentRef}
        className="flex min-w-0 w-full flex-1 flex-col gap-4 overflow-y-auto pb-4 pt-8"
      >
        <QuizFeedback
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
          positiveFeedback={currentEntry.userFeedback?.positive}
          negativeFeedback={currentEntry.userFeedback?.negative}
          className="w-full max-w-2xl"
        />
        <div className="flex min-w-0 w-full flex-col gap-4">
          <TaskInstructionHint>Wähle die richtige Option</TaskInstructionHint>
          <div className="min-w-0 w-full pl-3 leading-relaxed text-primary">
            {textParts.map((part, index) => {
              const selectedLabel = userAnswers[index] || "";
              // Empty gaps follow the authored blank; a selected option sizes
              // the control so the closed label stays readable.
              const gapWidthLabel = selectedLabel || currentEntry.gapMark;

              return (
                <Fragment key={index}>
                  {part}
                  {index < textParts.length - 1 && (
                    // Invisible label in normal flow sets the width; the select
                    // is stretched over it so long <option>s cannot force a wrap.
                    // Hide the native chevron so the selected label is not clipped.
                    <span className="relative mx-1 inline-block max-w-full align-baseline">
                      <span
                        aria-hidden="true"
                        className="invisible inline-block whitespace-pre border-2 border-transparent py-1 pl-3 pr-10 font-sans text-base"
                      >
                        {gapWidthLabel}
                      </span>
                      <select
                        value={selectedLabel}
                        onChange={(e) =>
                          handleInputChange(index, e.target.value)
                        }
                        disabled={isSubmitted}
                        className={cn(
                          "absolute inset-0 h-full w-full appearance-none truncate border-2 rounded-md py-1 pl-3 pr-10 font-sans text-base text-primary",
                          "focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary",
                          "disabled:cursor-not-allowed transition-colors",
                          getSelectStyles(index)
                        )}
                      >
                        <option value="" disabled>
                          Wähle eine Option
                        </option>
                        {(currentEntry.gaps?.[index]?.options ?? []).map(
                          (option, optionIndex) => (
                            <option key={optionIndex} value={option}>
                              {option}
                            </option>
                          )
                        )}
                      </select>
                      <ChevronDown
                        aria-hidden="true"
                        className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-primary"
                      />
                    </span>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <div className="shrink-0 pt-4">
        <div className="flex justify-center">
          {/* eslint-disable-next-line no-nested-ternary */}
          {!isSubmitted ? (
            <QuizActionButton
              variant="default"
              onClick={handleSubmit}
              disabled={!hasAllAnswers}
            >
              {submitLabel}
            </QuizActionButton>
          ) : currentIndex < entries.length - 1 ? (
            <QuizActionButton onClick={handleNext}>
              Nächste Frage
            </QuizActionButton>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default QuizGapwordDropdown;
